import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Match {
  id: string;
  leagueId: string;
  homeTeamId: string;
  awayTeamId: string;
  scheduledDate: string;
  scheduledTime: string;
  location: string;
  status: string;
  homeScore?: number;
  awayScore?: number;
  homeTeam: { id: string; name: string } | null;
  awayTeam: { id: string; name: string } | null;
  league: { id: string; name: string } | null;
}

const RefereeDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'completed'>('all');

  useEffect(() => {
    if (user?.profileType !== 'referee') {
      navigate('/dashboard');
      return;
    }

    fetchMatches();
  }, [user, navigate, filter]);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await api.get('/referees/matches');
      let fetchedMatches = response.data.matches || response.data || [];
      
      // Apply filter
      if (filter === 'scheduled') {
        fetchedMatches = fetchedMatches.filter((m: Match) => m.status === 'scheduled');
      } else if (filter === 'completed') {
        fetchedMatches = fetchedMatches.filter((m: Match) => m.status === 'completed');
      }
      
      // Sort by date
      fetchedMatches.sort((a: Match, b: Match) => {
        const dateA = new Date(`${a.scheduledDate}T${a.scheduledTime}`);
        const dateB = new Date(`${b.scheduledDate}T${b.scheduledTime}`);
        return dateA.getTime() - dateB.getTime();
      });
      
      setMatches(fetchedMatches);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <h1>Referee Dashboard</h1>
      <p>Welcome, {user?.firstName} {user?.lastName}</p>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
        <button
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
        >
          All Matches
        </button>
        <button
          className={`btn ${filter === 'scheduled' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('scheduled')}
        >
          Scheduled
        </button>
        <button
          className={`btn ${filter === 'completed' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('completed')}
        >
          Completed
        </button>
      </div>

      {matches.length === 0 ? (
        <div className="card">
          <p>No matches assigned to you.</p>
        </div>
      ) : (
        <div className="grid">
          {matches.map((match) => (
            <div key={match.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                <div>
                  <h3>
                    {match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}
                  </h3>
                  {match.league && (
                    <p style={{ color: '#757575', fontSize: '14px' }}>{match.league.name}</p>
                  )}
                </div>
                <span className={`badge ${match.status === 'completed' ? 'badge-success' : match.status === 'scheduled' ? 'badge-info' : 'badge-warning'}`}>
                  {match.status}
                </span>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <p><strong>Date:</strong> {new Date(match.scheduledDate).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {match.scheduledTime}</p>
                <p><strong>Location:</strong> {match.location}</p>
                {match.status === 'completed' && match.homeScore !== undefined && match.awayScore !== undefined && (
                  <p><strong>Score:</strong> {match.homeScore} - {match.awayScore}</p>
                )}
              </div>

              {match.status === 'scheduled' && (
                <Link
                  to={`/referees/matches/${match.id}/complete`}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  Complete Match
                </Link>
              )}
              {(match.status === 'completed' || match.status === 'scheduled') && (
                <Link
                  to={`/referees/matches/${match.id}`}
                  className="btn btn-secondary"
                  style={{ width: '100%', marginTop: '10px' }}
                >
                  View Details
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RefereeDashboard;

